import React, { useMemo, useState } from "react";
import GlassCard from "@/components/common/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TABS = [
  { id: "sites", label: "Sites" },
  { id: "experiments", label: "A/B tests" },
  { id: "competitors", label: "Competitors" },
  { id: "citations", label: "Citations" },
];

export default function SEOLabs({
  data,
  pages,
  onRefresh,
  onCreateSite,
  onActivateSite,
  onCreateExperiment,
  onApplyVariant,
  onSyncExperiment,
  onAddCompetitor,
  onFetchCompetitors,
  onAddCitation,
  onProbeCitation,
}) {
  const [tab, setTab] = useState("sites");
  const stats = data?.stats || {};

  return (
    <div className="space-y-4 w-full max-w-[1600px]">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">Labs (optional)</h3>
        <p className="text-xs text-white/50">
          Power tools only. Day-to-day work is Start here → Pages → Issues. Skip this unless you need A/B tests or multi-site.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-white/70">
          <div><span className="text-white/40 block">Sites</span>{stats.sites ?? 0}</div>
          <div><span className="text-white/40 block">Running tests</span>{stats.experiments_running ?? 0}</div>
          <div><span className="text-white/40 block">Competitor rows</span>{stats.competitors ?? 0}</div>
          <div><span className="text-white/40 block">Citation hits</span>{stats.citation_mentions ?? 0}/{stats.citations ?? 0}</div>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {TABS.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant="ghost"
              className={tab === t.id ? "text-white bg-white/10" : "text-white/60"}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" className="text-white/50 ml-auto" onClick={onRefresh}>Refresh</Button>
        </div>
      </GlassCard>

      {tab === "sites" && (
        <SitesPanel
          sites={data?.sites || []}
          active={data?.active_site}
          onCreate={onCreateSite}
          onActivate={onActivateSite}
        />
      )}
      {tab === "experiments" && (
        <ExperimentsPanel
          experiments={data?.experiments || []}
          pages={pages || []}
          onCreate={onCreateExperiment}
          onApply={onApplyVariant}
          onSync={onSyncExperiment}
        />
      )}
      {tab === "competitors" && (
        <CompetitorsPanel
          items={data?.competitors || []}
          serpConfigured={data?.serp_configured}
          onAdd={onAddCompetitor}
          onFetch={onFetchCompetitors}
        />
      )}
      {tab === "citations" && (
        <CitationsPanel
          items={data?.citations || []}
          engines={data?.citation_engines || []}
          onAdd={onAddCitation}
          onProbe={onProbeCitation}
        />
      )}
    </div>
  );
}

function SitesPanel({ sites, active, onCreate, onActivate }) {
  const [name, setName] = useState("");
  const [origin, setOrigin] = useState("https://");
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">Add property</h3>
        <p className="text-xs text-white/45">Agency scaffolding — switch active site to align production origin.</p>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Site name" className="bg-black/20 border-white/10 text-white" />
        <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="https://example.com" className="bg-black/20 border-white/10 text-white" />
        <Button
          className="btn-gradient text-white border-0"
          onClick={() => {
            onCreate?.({ name, origin });
            setName("");
          }}
        >
          Add site
        </Button>
      </GlassCard>
      <GlassCard className="p-5 space-y-2">
        {(sites || []).map((s) => (
          <div key={s.id} className="flex items-center gap-3 text-sm text-white/75 border-b border-white/5 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-white truncate">{s.name} {s.active || s.id === active?.id ? <span className="text-emerald-300/90 text-xs">· active</span> : null}</div>
              <div className="text-xs text-white/40 font-mono truncate">{s.origin}</div>
            </div>
            {!s.active && s.id !== active?.id && (
              <Button size="sm" variant="ghost" className="text-cyan-300/90" onClick={() => onActivate?.(s.id)}>Activate</Button>
            )}
          </div>
        ))}
      </GlassCard>
    </div>
  );
}

function ExperimentsPanel({ experiments, pages, onCreate, onApply, onSync }) {
  const pageOptions = useMemo(() => (pages || []).slice(0, 80), [pages]);
  const [pageId, setPageId] = useState("");
  const [name, setName] = useState("Title/meta test");
  const [aTitle, setATitle] = useState("");
  const [aDesc, setADesc] = useState("");
  const [bTitle, setBTitle] = useState("");
  const [bDesc, setBDesc] = useState("");

  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">New A/B title–meta test</h3>
        <p className="text-xs text-white/45">
          Sequential tests: Apply a variant → Publish → wait for GSC data → Sync CTR → compare. Winner is suggested at ≥30 impressions.
        </p>
        <select
          value={pageId}
          onChange={(e) => setPageId(e.target.value)}
          className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2"
        >
          <option value="">Select page…</option>
          {pageOptions.map((p) => (
            <option key={p.id} value={p.id}>{p.path} — {p.name}</option>
          ))}
        </select>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Experiment name" />
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <p className="text-[11px] uppercase text-white/40">Variant A</p>
            <Input value={aTitle} onChange={(e) => setATitle(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Title A" />
            <Input value={aDesc} onChange={(e) => setADesc(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Meta description A" />
          </div>
          <div className="space-y-2">
            <p className="text-[11px] uppercase text-white/40">Variant B</p>
            <Input value={bTitle} onChange={(e) => setBTitle(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Title B" />
            <Input value={bDesc} onChange={(e) => setBDesc(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Meta description B" />
          </div>
        </div>
        <Button
          className="btn-gradient text-white border-0"
          onClick={() => onCreate?.({
            page_id: pageId,
            name,
            variants: [
              { id: "v1", label: "A", title: aTitle, description: aDesc },
              { id: "v2", label: "B", title: bTitle, description: bDesc },
            ],
          })}
        >
          Create experiment
        </Button>
      </GlassCard>

      {(experiments || []).map((exp) => (
        <GlassCard key={exp.id} className="p-5 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-white">
            <span>{exp.name}</span>
            <span className="text-xs text-white/40">{exp.status}</span>
            {exp.winner_id && <span className="text-xs text-emerald-300/90">winner {exp.winner_id}</span>}
          </div>
          <div className="text-xs text-white/50">Page {exp.page_id} · active {exp.active_variant_id}</div>
          <div className="flex flex-wrap gap-2">
            {(exp.variants || []).map((v) => (
              <Button key={v.id} size="sm" variant="ghost" className="text-cyan-300/90" onClick={() => onApply?.(exp.id, v.id)}>
                Apply {v.label || v.id}
              </Button>
            ))}
            <Button size="sm" variant="ghost" className="text-white/70" onClick={() => onSync?.(exp.id)}>Sync GSC CTR</Button>
          </div>
          {(exp.metrics || []).length > 0 && (
            <div className="text-xs text-white/60 space-y-1 pt-1">
              {exp.metrics.map((m, i) => (
                <div key={`${m.variant_id}-${i}`}>
                  {m.variant_id}: CTR {m.ctr}% · clicks {m.clicks} · imps {m.impressions} · pos {m.position}
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      ))}
      {!(experiments || []).length && <p className="text-sm text-white/40">No experiments yet.</p>}
    </div>
  );
}

function CompetitorsPanel({ items, serpConfigured, onAdd, onFetch }) {
  const [query, setQuery] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">SERP / competitors</h3>
        <p className="text-xs text-white/45">
          {serpConfigured
            ? "SERPAPI_KEY detected — Fetch pulls live Google organic snippets."
            : "No SERPAPI_KEY — add competitors manually, or set SERPAPI_KEY on the backend for live pulls."}
        </p>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Target query" />
        <div className="flex flex-wrap gap-2">
          <Button className="btn-gradient text-white border-0" onClick={() => onFetch?.({ query })}>Fetch SERP</Button>
        </div>
        <div className="grid md:grid-cols-2 gap-2 pt-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Competitor title" />
          <Input value={url} onChange={(e) => setUrl(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="URL" />
        </div>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Snippet / description" />
        <Button
          variant="ghost"
          className="text-white/70"
          onClick={() => {
            onAdd?.({ query, title, description, url });
            setTitle("");
            setDescription("");
            setUrl("");
          }}
        >
          Add manual row
        </Button>
      </GlassCard>
      <GlassCard className="p-5 space-y-2">
        {(items || []).map((c) => (
          <div key={c.id} className="border-b border-white/5 py-2 text-sm text-white/75">
            <div className="text-white">{c.title}</div>
            <div className="text-xs text-white/45">{c.query} · {c.source}{c.position != null ? ` · #${c.position}` : ""}</div>
            <div className="text-xs text-white/50 mt-1">{c.description}</div>
            {c.url && <a className="text-xs text-cyan-300/80 break-all" href={c.url} target="_blank" rel="noreferrer">{c.url}</a>}
          </div>
        ))}
        {!(items || []).length && <p className="text-sm text-white/40">No competitor snippets yet.</p>}
      </GlassCard>
    </div>
  );
}

function CitationsPanel({ items, engines, onAdd, onProbe }) {
  const [query, setQuery] = useState("");
  const [engine, setEngine] = useState("chatgpt");
  const [mentioned, setMentioned] = useState(true);
  const [snippet, setSnippet] = useState("");
  return (
    <div className="space-y-4">
      <GlassCard className="p-5 space-y-3">
        <h3 className="text-sm text-white">AI citation tracker</h3>
        <p className="text-xs text-white/45">
          Log when ChatGPT / Perplexity / Gemini quote you. Probe runs a Gemini GEO self-check (not a live ChatGPT scrape).
        </p>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Prompt / query to track" />
        <div className="flex flex-wrap gap-2 items-center">
          <select value={engine} onChange={(e) => setEngine(e.target.value)} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
            {(engines || ["chatgpt", "perplexity", "gemini", "manual"]).map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
          <label className="text-sm text-white/70 flex items-center gap-2">
            <input type="checkbox" checked={mentioned} onChange={(e) => setMentioned(e.target.checked)} />
            Mentioned
          </label>
        </div>
        <Input value={snippet} onChange={(e) => setSnippet(e.target.value)} className="bg-black/20 border-white/10 text-white" placeholder="Quoted snippet (optional)" />
        <div className="flex flex-wrap gap-2">
          <Button
            className="btn-gradient text-white border-0"
            onClick={() => onAdd?.({ query, engine, mentioned, snippet })}
          >
            Log citation
          </Button>
          <Button variant="ghost" className="text-white/70" onClick={() => onProbe?.({ query, save: true })}>
            Gemini probe + save
          </Button>
        </div>
      </GlassCard>
      <GlassCard className="p-5 space-y-2">
        {(items || []).map((c) => (
          <div key={c.id} className="border-b border-white/5 py-2 text-sm">
            <div className="text-white flex gap-2 items-center">
              <span className={c.mentioned ? "text-emerald-300" : "text-white/40"}>{c.mentioned ? "cited" : "miss"}</span>
              <span className="text-white/50 text-xs">{c.engine}</span>
              <span className="truncate">{c.query}</span>
            </div>
            {c.snippet && <p className="text-xs text-white/55 mt-1">{c.snippet}</p>}
          </div>
        ))}
        {!(items || []).length && <p className="text-sm text-white/40">No citation logs yet.</p>}
      </GlassCard>
    </div>
  );
}
